// src/components/personal/sections/OrdersSection/components/OrderInfo/OrderItemsTab.tsx
import React from 'react';
import { Order } from '../../types/types';
// Удалено неиспользуемый импорт TEXT_STYLES
import { TFunction } from 'i18next';
import { useBusinessCurrency } from '@/hooks/useBusinessCurrency';
import { Table, TableColumn } from '@/components/ui/Table/Table';
import { useColumnWidths } from '@/hooks/useColumnWidths';

interface OrderItemsTabProps {
  order: Order;
  t: TFunction;
}

const OrderItemsTab: React.FC<OrderItemsTabProps> = ({ order, t }) => {
  const { formatPrice } = useBusinessCurrency();
  
  // Используем хук для сохранения ширины столбцов
  const [columnWidths, setColumnWidths] = useColumnWidths('order-items-table');
  
  // Безопасное извлечение данных cost_data
  const subtotal = order.cost_data?.subtotal || 0;
  const shippingFee = order.cost_data?.shipping_fee || 0;
  const total = order.cost_data?.total || 0;
  
  // Вычисление суммы всех скидок
  const getDiscountsTotal = () => {
    if (!order.cost_data?.discounts) return 0;
    
    return Object.entries(order.cost_data.discounts).reduce((sum, [, value]) => {
      // Удалена неиспользуемая переменная key
      const numValue = typeof value === 'number' ? value : parseFloat(value || '0');
      return sum + (isNaN(numValue) ? 0 : numValue);
    }, 0);
  };
  
  const totalDiscount = getDiscountsTotal();
  
  // Удалена неиспользуемая переменная loyaltyBonusApplied

  // Определяем колонки для таблицы
  const columns: TableColumn<any>[] = [
    {
      key: 'name',
      title: t('table.name'),
      width: 'xl',
      resizable: true,
      wrap: true,
      render: (item) => item.name
    },
    {
      key: 'quantity',
      title: t('table.quantity'),
      width: 'sm',
      resizable: true,
      textAlign: 'center',
      render: (item) => item.quantity
    },
    {
      key: 'price',
      title: t('table.price'),
      width: 'sm',
      resizable: true,
      textAlign: 'right',
      render: (item) => (parseFloat(item.price)).toLocaleString()
    },
    {
      key: 'total',
      title: t('table.total'),
      width: 'sm',
      resizable: true,
      textAlign: 'right',
      render: (item) => (parseFloat(item.total)).toLocaleString()
    }
  ];

  // Функция для сброса ширины столбцов к значениям по умолчанию
  const resetColumnWidths = () => {
    setColumnWidths({});
  };

  // Безопасный доступ к order_data
  const orderItems = order.order_data || [];

  if (orderItems.length === 0) {
    return (
      <div className="text-center py-4 text-secondary">
        {t('order_details.no_items')}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Кнопка для сброса настроек столбцов */}
      {Object.keys(columnWidths).length > 0 && (
        <div className="flex justify-end mb-2">
          <button 
            onClick={resetColumnWidths}
            className="text-xs text-gray-500 hover:text-primary transition-colors flex items-center"
            title={t('common:reset_to_default')}
          >
            <svg className="w-3 h-3 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
            </svg>
            {t('common:reset_columns')}
          </button>
        </div>
      )}

      <Table
        data={orderItems}
        columns={columns}
        emptyText={t('order_details.no_items')}
        scrollable={false} // Изменено на false, как в ClientOrders
        resizableColumns={true}
        savedColumnWidths={columnWidths}
        onColumnWidthsChange={setColumnWidths}
      />

      <div className="flex justify-end items-center bg-background p-3 rounded-lg">
        <div className="grid grid-cols-2 gap-2 w-full max-w-xs">
          <div className="text-right">{t('order_details.subtotal')}:</div>
          <div className="text-right font-medium">{formatPrice(subtotal)}</div>
          
          {totalDiscount > 0 && (
            <>
              <div className="text-right text-green-600">{t('order_details.discounts')}:</div>
              <div className="text-right font-medium text-green-600">-{formatPrice(totalDiscount)}</div>
            </>
          )}
          
          {shippingFee > 0 && (
            <>
              <div className="text-right">{t('order_details.shipping_fee')}:</div>
              <div className="text-right font-medium">{formatPrice(shippingFee)}</div>
            </>
          )}
          
          <div className="text-right font-bold border-t pt-1">{t('table.total')}:</div>
          <div className="text-right font-bold text-primary border-t pt-1">{formatPrice(total)}</div>
        </div>
      </div>
    </div>
  );
};

export default OrderItemsTab;